import { api } from '../client';
import type {
  PaymentRebuildStart,
  PaymentRebuildResult,
  PaymentMetricsResponse,
  PaymentMetricsRange,
  PaymentMetricsBreaches,
  MetricsBackfillResult,
  InvoiceIssueRequest,
  InvoiceResponse,
  InvoiceSnapshot,
  GstMode,
  AddChargeRequest,
  ApproveChargeRequest,
  VerifyClaimRequest,
  ChargeResponse,
  MarkPaidRequest,
  OpenItemsResponse,
  PaymentAllocationLine,
  PaymentAllocationResponse,
  PaymentDirection,
  PaymentExtendedErrorCode,
  PaymentQueueResponse,
  PaymentResponse,
  PaymentTransactionFilters,
  PaymentTransactionsResponse,
  PayoutRequest,
  RecordReceiptRequest,
  OrderMoneyStaffSummary,
  PaymentTerms,
  ShipperPaymentTermsRequest,
  ShipperPaymentTermsResponse,
  AbsorbRequest,
  InvoiceReadiness,
  PaidNotBilledResponse,
  PaidNotBilledRow,
} from '../types';
import { uploadFileDirectToS3 } from './uploads';

export const PAYMENT_MESSAGES: Record<PaymentExtendedErrorCode, string> = {
  ABSORB_REASON_REQUIRED: 'Pick a reason. For Other, add a short note.',
  SELF_APPROVAL_NOT_ALLOWED: 'You made this request, so someone else has to approve it.',
  ESCALATION_REASON_REQUIRED: 'This is above the limit. Add a short reason (up to 200 characters).',
  CLAIM_STATE_CHANGED: 'This claim has already been decided. Refresh payment details.',
  CLAIM_EXCEEDS_OUTSTANDING:
    'Another payment or claim changed the available invoice balance. Refresh and try again.',
  PAID_AT_FUTURE: 'The paid date cannot be in the future.',
  LEGACY_ORDER: 'Payments are unavailable for this order before the payments start date.',
  EXEMPTION_REASON_REQUIRED: 'Enter a reason for exemption.',
  INVALID_EXEMPTION_REASON: 'Reason for exemption must be at most 500 characters.',
  EXEMPTION_CONFIRMATION_REQUIRED:
    'Confirm that this transport service qualifies for exemption. Missing GSTIN alone is not an exemption.',
  EXEMPTION_STATE_REQUIRED: 'Select the recipient state.',
  INVALID_EXEMPTION_REFERENCE: 'Exemption reference must be at most 200 characters.',
  EXEMPTION_GST_CONFLICT: 'Exemption cannot include an FCM or RCM option.',
  EXEMPTION_TREATMENT_REQUIRED: 'Select explicit exempt treatment.',
  EXEMPTION_STATE_MISMATCH: 'Recipient state must match the saved GSTIN.',
  EXEMPTION_BUYER_DETAILS_REQUIRED: 'Customer name, billing address and state are required.',
  SHIPPER_GSTIN_REQUIRED:
    'First ask the customer for their GSTIN and add it. A missing GSTIN does not necessarily mean this transport service is GST exempt.',
  SHIPPER_GSTIN_INVALID: 'Correct the buyer GSTIN before generating an invoice.',
  GST_OPTION_NOT_ALLOWED: 'This GST option is no longer allowed. Close the dialog and try again.',
  INVOICE_NOT_FOUND: 'This invoice is no longer available.',
  INVOICE_STATE_CHANGED: 'This invoice changed. Refresh and check its current status.',
  INVALID_INVOICE_REFERENCES:
    'Keep reference text within 60 characters, packages within 0–1,000,000, and LR date no later than today.',
  PAYEE_MISMATCH: 'Every selected order must belong to the same partner.',
  PAYEE_NOT_READY: 'This partner is not ready for payouts. Complete KYC and bank details first.',
  PAYEE_NOT_SUPPORTED: 'This order does not have a supported fleet-manager payee.',
  ADVANCE_NOT_AVAILABLE: 'Advance is available only after the order is loaded.',
  CHARGES_PENDING:
    'Approve or reject pending charges before requesting the balance or generating an invoice.',
  PAYMENT_OVER_ALLOCATED: 'The allocations exceed the payment amount.',
  CREDIT_EXCEEDED: 'The allocation exceeds the available unallocated credit.',
  REBUILD_IN_PROGRESS:
    'A maintenance run is already in progress (rebuild or nightly check). Try again in a few minutes.',
  FORBIDDEN: 'A manager must complete this payment action.',
  PROOF_UPLOAD_INVALID: 'The proof could not be verified. Attach it again.',
  PAYMENT_STATE_CHANGED: 'This payment changed. Refresh and check its current status.',
  IDEMPOTENCY_RETRY: 'The previous submission is still being confirmed. Try again shortly.',
  IDEMPOTENCY_CONFLICT:
    'This form was already submitted with different details. Close it and start again.',
  INVOICE_ALREADY_ISSUED:
    'An invoice is already issued. Cancel it before reissuing or adding shipper charges.',
  BALANCE_ALREADY_REQUESTED:
    'A partner balance is already requested, so this charge cannot be added.',
  ORDER_NOT_LOADED: 'This action is available only after the order is loaded.',
  ADVANCE_AFTER_POD: 'POD verified — request the remaining amount as balance.',
  BALANCE_BEFORE_POD: 'Balance is available only after POD verification.',
  ADVANCE_EXCEEDED: 'The advance amount exceeds what is available for this order.',
  ALLOCATIONS_EXCEED_AMOUNT: 'The allocations exceed the payment amount.',
  ALLOCATION_NOT_FOUND: 'This allocation is no longer available.',
  ALLOCATION_STATE_CHANGED: 'This allocation changed. Refresh and check its current status.',
  AMOUNT_NOT_WHOLE_RUPEES: 'Charge amounts must be entered in whole rupees.',
  CANNOT_VOID_OUT_ALLOCATION: 'Void the entire payout instead of one payout allocation.',
  CHARGE_NOT_FOUND: 'This charge is no longer available.',
  CHARGE_STATE_CHANGED: 'This charge changed. Refresh and check its current status.',
  CLIENT_REQUEST_ID_REQUIRED: 'Close this form and open it again before submitting.',
  DATE_RANGE_REQUIRED: 'Choose both a from date and a to date.',
  DESCRIPTION_REQUIRED: 'Add a description for this charge.',
  DESCRIPTION_TOO_LONG: 'Keep the charge description within 200 characters.',
  INVALID_ALLOCATION_AMOUNT: 'Enter a valid cash or TDS amount for each selected order.',
  INVALID_ALLOCATION_COUNT: 'Select between 1 and 50 allocation lines.',
  INVALID_ALLOCATION_LINE: 'Check every selected allocation line and try again.',
  INVALID_AMOUNT: 'Enter an amount greater than zero.',
  INVALID_CHARGE_TYPE: 'Choose a valid charge type.',
  INVALID_METRICS_RANGE: 'Choose a valid metrics range of 1–92 IST days.',
  INVALID_DATE_RANGE: 'Choose a valid date range of no more than 92 days.',
  INVALID_PAID_AT: 'The paid date and time cannot be in the future.',
  INVALID_PAGE: 'This page is not available. Choose a valid page with up to 50 rows.',
  INVALID_PARAMS: 'Choose a direction and counterparty.',
  INVALID_PAYMENT_STATE: 'This action is not available for the payment’s current status.',
  INVALID_PURPOSE: 'Choose a valid allocation purpose.',
  INVALID_REASON: 'Enter a reason between 3 and 500 characters.',
  INVALID_REQUEST: 'Check the entered information and try again.',
  METHOD_REQUIRED: 'Choose a payment method.',
  NOTE_REQUIRED: 'Add an audit note of at least 3 characters.',
  NOTE_TOO_LONG: 'Keep the audit note within 500 characters.',
  ORDER_CANCELLED: 'Money cannot be added to a cancelled order.',
  ORDER_NOT_FOUND: 'This order is no longer available.',
  ORDER_REQUIRED: 'Choose an order for every allocation.',
  PARTNER_NOT_FOUND: 'This partner is no longer available.',
  PARTNER_REQUIRED: 'Choose a partner before requesting a payout.',
  PAYMENT_NOT_FOUND: 'This payment is no longer available.',
  POD_NOT_VERIFIED:
    'The balance and invoice are available after the proof of delivery is verified.',
  PROOF_REQUIRED: 'Attach payment proof before saving.',
  PROOF_NOT_FOUND: 'This payment proof is no longer available.',
  REASON_REQUIRED: 'Enter a reason between 3 and 500 characters.',
  SHIPPER_MISMATCH: 'Every selected order must belong to the same shipper.',
  SHIPPER_NOT_FOUND: 'This shipper is no longer available.',
  SHIPPER_REQUIRED: 'Choose a shipper before recording a receipt.',
  UTR_REQUIRED: 'Enter a valid bank reference or UTR.',
  SHIPMENT_NOT_FOUND: 'This shipment is no longer available.',
  INVALID_BILL_NUMBER: 'Enter a partner bill number between 1 and 40 characters.',
  INVALID_PAYMENT_TERMS: 'Payment terms must be between 0 and 180 days.',
  INVALID_GST_PAIR: 'Choose both a GST mode and rate.',
  GST_PAIR_NOT_ALLOWED: 'Choose one of the GST options allowed in payment settings.',
  AMOUNT_TOO_PRECISE: 'Amounts support at most 2 decimal places.',
  UNAUTHORIZED: 'Your session cannot complete this payment action. Sign in again.',
};

export class PaymentRequestError extends Error {
  constructor(
    message: string,
    readonly code?: PaymentExtendedErrorCode,
    readonly status?: number,
    readonly shipmentId?: number,
  ) {
    super(message);
    this.name = 'PaymentRequestError';
  }
}

function errorDetail(error: unknown): { code?: PaymentExtendedErrorCode; status?: number } {
  if (!error || typeof error !== 'object') return {};
  const candidate = error as {
    code?: PaymentExtendedErrorCode;
    response?: { status?: number; data?: { code?: PaymentExtendedErrorCode } };
  };
  return {
    code: candidate.code ?? candidate.response?.data?.code,
    status: candidate.response?.status,
  };
}

export function mapPaymentError(error: unknown): string {
  const { code } = errorDetail(error);
  if (code && PAYMENT_MESSAGES[code]) return PAYMENT_MESSAGES[code];
  if (error instanceof Error && error.message) return error.message;
  return 'We couldn’t complete this payment action. Refresh and try again.';
}

function normalizeError(error: unknown): PaymentRequestError {
  const { code, status } = errorDetail(error);
  const identity = (error as { shipmentId?: unknown } | null)?.shipmentId;
  return new PaymentRequestError(
    mapPaymentError(error),
    code,
    status,
    typeof identity === 'number' && Number.isSafeInteger(identity) ? identity : undefined,
  );
}

async function createWithOneIdempotencyRetry<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (errorDetail(error).code === 'IDEMPOTENCY_RETRY') {
      try {
        return await request();
      } catch (retryError) {
        throw normalizeError(retryError);
      }
    }
    throw normalizeError(error);
  }
}

export async function uploadPaymentProof(file: File): Promise<string> {
  const completed = await uploadFileDirectToS3({ file, purpose: 'PAYMENT_PROOF' });
  return completed.intentId;
}

export async function uploadChargeAttachment(file: File, shipmentId: number): Promise<string> {
  const completed = await uploadFileDirectToS3({
    file,
    purpose: 'CHARGE_ATTACHMENT',
    targetId: shipmentId,
  });
  return completed.intentId;
}

export async function getOrderMoneySummary(shipmentId: number): Promise<OrderMoneyStaffSummary> {
  const { data } = await api.get<OrderMoneyStaffSummary>(`/admin/shipments/${shipmentId}/payments`);
  return data;
}

export async function setPartnerBill(shipmentId: number, billNumber: string): Promise<void> {
  await api.put(`/admin/shipments/${shipmentId}/partner-bill`, { billNumber });
}

export async function getPaymentTerms(): Promise<PaymentTerms> {
  const { data } = await api.get<PaymentTerms>('/admin/settings/payment-terms');
  return data;
}

export async function updatePaymentTerms(request: PaymentTerms): Promise<PaymentTerms> {
  const { data } = await api.put<PaymentTerms>('/admin/settings/payment-terms', request);
  return data;
}

export async function updateShipperPaymentTerms(
  userId: number,
  request: ShipperPaymentTermsRequest,
): Promise<ShipperPaymentTermsResponse> {
  const { data } = await api.put<ShipperPaymentTermsResponse>(
    `/admin/shippers/${userId}/payment-terms`,
    request,
  );
  return data;
}

export async function getOpenPaymentItems(
  direction: PaymentDirection,
  counterpartyId: number,
  options?: { page: number; size: number; q?: string },
): Promise<OpenItemsResponse> {
  const { data } = await api.get<OpenItemsResponse>('/admin/payments/open-items', {
    params: { direction, counterpartyId, ...options },
  });
  return data;
}

export async function getPaymentQueue(mine: boolean): Promise<PaymentQueueResponse> {
  const { data } = await api.get<PaymentQueueResponse>('/admin/payments/queue', {
    params: { mine },
  });
  return data;
}

export async function getTransactions(
  filters: PaymentTransactionFilters,
): Promise<PaymentTransactionsResponse> {
  const { data } = await api.get<PaymentTransactionsResponse>('/admin/payments', {
    params: filters,
  });
  return data;
}

export async function getPayment(paymentId: number): Promise<PaymentResponse> {
  const { data } = await api.get<PaymentResponse>(`/admin/payments/${paymentId}`);
  return data;
}

export async function getPaymentProof(paymentId: number): Promise<string> {
  const { data } = await api.get<{ url: string }>(`/admin/payments/${paymentId}/proof`);
  return data.url;
}

export async function recordReceipt(request: RecordReceiptRequest): Promise<PaymentResponse> {
  return createWithOneIdempotencyRetry(async () => {
    const { data } = await api.post<PaymentResponse>('/admin/payments/receipts', request);
    return data;
  });
}

export async function requestPayout(request: PayoutRequest): Promise<PaymentResponse> {
  return createWithOneIdempotencyRetry(async () => {
    const { data } = await api.post<PaymentResponse>('/admin/payments/payouts', request);
    return data;
  });
}

export async function allocatePaymentCredit(
  paymentId: number,
  allocations: PaymentAllocationLine[],
): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(`/admin/payments/${paymentId}/allocations`, {
    allocations,
  });
  return data;
}

export async function approvePayment(paymentId: number): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(`/admin/payments/${paymentId}/approve`);
  return data;
}

export async function rejectPayment(paymentId: number, reason: string): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(`/admin/payments/${paymentId}/reject`, {
    reason,
  });
  return data;
}

/** Reject a payout from My approvals with the approver's note (#297 Contract). */
export async function rejectApproval(paymentId: number, note: string): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(`/admin/payments/${paymentId}/reject`, { note });
  return data;
}

export async function markPaymentPaid(
  paymentId: number,
  request: MarkPaidRequest,
): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(
    `/admin/payments/${paymentId}/mark-paid`,
    request,
  );
  return data;
}

export async function voidPayment(paymentId: number, reason: string): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(`/admin/payments/${paymentId}/void`, { reason });
  return data;
}

export async function voidPaymentAllocation(
  allocationId: number,
  reason: string,
): Promise<PaymentAllocationResponse> {
  const { data } = await api.post<PaymentAllocationResponse>(
    `/admin/payment-allocations/${allocationId}/void`,
    { reason },
  );
  return data;
}

export async function addCharge(
  shipmentId: number,
  request: AddChargeRequest,
): Promise<ChargeResponse> {
  return createWithOneIdempotencyRetry(async () => {
    const { data } = await api.post<ChargeResponse>(
      `/admin/shipments/${shipmentId}/charges`,
      request,
    );
    return data;
  });
}

export async function approveCharge(
  chargeId: number,
  request?: ApproveChargeRequest,
): Promise<ChargeResponse> {
  const { data } = request
    ? await api.post<ChargeResponse>(`/admin/charges/${chargeId}/approve`, request)
    : await api.post<ChargeResponse>(`/admin/charges/${chargeId}/approve`);
  return data;
}

export async function rejectCharge(chargeId: number, reason: string): Promise<ChargeResponse> {
  const { data } = await api.post<ChargeResponse>(`/admin/charges/${chargeId}/reject`, { reason });
  return data;
}

export async function voidCharge(chargeId: number, reason: string): Promise<ChargeResponse> {
  const { data } = await api.post<ChargeResponse>(`/admin/charges/${chargeId}/void`, { reason });
  return data;
}

export async function previewInvoice(
  shipmentId: number,
  gstMode?: GstMode,
  gstRate?: number,
): Promise<InvoiceSnapshot> {
  try {
    const { data } = await api.get<InvoiceSnapshot>(
      `/admin/shipments/${shipmentId}/invoice-preview`,
      { params: { gstMode, gstRate } },
    );
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function previewExplicitInvoice(
  shipmentId: number,
  request: InvoiceIssueRequest,
): Promise<InvoiceSnapshot> {
  try {
    const { data } = await api.post<InvoiceSnapshot>(
      `/admin/shipments/${shipmentId}/invoice-preview`,
      request,
    );
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function issueInvoice(
  shipmentId: number,
  request: InvoiceIssueRequest,
): Promise<InvoiceResponse> {
  return createWithOneIdempotencyRetry(async () => {
    const { data } = await api.post<InvoiceResponse>(
      `/admin/shipments/${shipmentId}/invoices`,
      request,
    );
    return data;
  });
}
export async function getInvoices(shipmentId: number): Promise<InvoiceResponse[]> {
  try {
    const { data } = await api.get<InvoiceResponse[]>(`/admin/shipments/${shipmentId}/invoices`);
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function getInvoice(id: number): Promise<InvoiceResponse> {
  try {
    const { data } = await api.get<InvoiceResponse>(`/admin/invoices/${id}`);
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function cancelInvoice(id: number, reason: string): Promise<InvoiceResponse> {
  try {
    const { data } = await api.post<InvoiceResponse>(`/admin/invoices/${id}/cancel`, { reason });
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function getPaymentMetrics(
  range: PaymentMetricsRange,
): Promise<PaymentMetricsResponse> {
  try {
    const { data } = await api.get<PaymentMetricsResponse>('/admin/payments/metrics', {
      params: range,
    });
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function getPaymentTeamMetrics(
  range: PaymentMetricsRange,
): Promise<PaymentMetricsResponse> {
  try {
    const { data } = await api.get<PaymentMetricsResponse>('/admin/payments/metrics/team', {
      params: range,
    });
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function getPaymentBreaches(
  filters: PaymentMetricsRange & { mine: boolean; page: number; size: number },
): Promise<PaymentMetricsBreaches> {
  try {
    const { data } = await api.get<PaymentMetricsBreaches>('/admin/payments/metrics/breaches', {
      params: filters,
    });
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
export async function backfillPaymentMetrics(
  range: PaymentMetricsRange,
): Promise<MetricsBackfillResult> {
  try {
    const { data } = await api.post<MetricsBackfillResult>(
      '/admin/payments/metrics/backfill',
      null,
      { params: range },
    );
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function rebuildOrderMoney(): Promise<PaymentRebuildStart> {
  try {
    const { data } = await api.post<PaymentRebuildStart>('/admin/payments/rebuild-summary');
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function getPaymentRebuildStatus(signal?: AbortSignal): Promise<PaymentRebuildResult> {
  try {
    const { data } = await api.get<PaymentRebuildResult>('/admin/payments/rebuild-summary', {
      signal,
    });
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function verifyPaymentClaim(
  paymentId: number,
  request: VerifyClaimRequest,
): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(
    `/admin/payments/${paymentId}/verify-claim`,
    request,
  );
  return data;
}
export async function rejectPaymentClaim(
  paymentId: number,
  reason: string,
): Promise<PaymentResponse> {
  const { data } = await api.post<PaymentResponse>(`/admin/payments/${paymentId}/reject-claim`, {
    reason,
  });
  return data;
}

export async function getCharge(chargeId: number): Promise<ChargeResponse> {
  const { data } = await api.get<ChargeResponse>(`/admin/charges/${chargeId}`);
  return data;
}
export async function getChargeProof(chargeId: number): Promise<string> {
  const { data } = await api.get<{ url: string }>(`/admin/charges/${chargeId}/proof`);
  return data.url;
}

// ─── Paid, not billed and invoice readiness (#293 Contract) ──────────────────

export async function listPaidNotBilled(options: {
  includeAbsorbed: boolean;
}): Promise<PaidNotBilledResponse> {
  const { data } = await api.get<PaidNotBilledResponse>('/admin/payments/paid-not-billed', {
    params: { includeAbsorbed: options.includeAbsorbed },
  });
  return data;
}

export async function addChargeToNextInvoice(chargeId: number): Promise<PaidNotBilledRow> {
  const { data } = await api.post<PaidNotBilledRow>(
    `/admin/payments/paid-not-billed/${chargeId}/add-to-next-invoice`,
  );
  return data;
}

export async function absorbCharge(
  chargeId: number,
  request: AbsorbRequest,
): Promise<PaidNotBilledRow> {
  const { data } = await api.post<PaidNotBilledRow>(
    `/admin/payments/paid-not-billed/${chargeId}/absorb`,
    request,
  );
  return data;
}

export async function getInvoiceReadiness(shipmentId: number): Promise<InvoiceReadiness> {
  const { data } = await api.get<InvoiceReadiness>(
    `/admin/shipments/${shipmentId}/invoice-readiness`,
  );
  return data;
}
